import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import ProposalSystemSummaryTab from "@/components/proposal/ProposalSystemSummaryTab";
import type { ConsolidatedSystemSummaryRow } from "@/components/proposal/ProposalConsolidatedSystemSummaryTable";
import type { DtoolsDisplaySettings } from "@shared/dtoolsDisplaySettings";
import { isDtoolsInternalImportSystem } from "@shared/dtoolsInternalSystems";
import { buildDtoolsReviewProposalTotals, buildDtoolsReviewSystemView, type DtoolsReviewBuildContext } from "@shared/dtoolsReviewNavigation";
import { systemContentKey } from "@shared/dtoolsProposalContent";
import type { DtoolsProposalPresentation } from "@shared/dtoolsProposalPresentation";
import {
  enrichProposalCustomerPricingWithSnapshot,
  scaleProposalCustomerPricingForSystemTotal,
  type ProposalCustomerPricingDisplay,
} from "@shared/proposalCustomerPricing";
import type { DtoolsReviewFooter, DtoolsReviewSystem } from "@shared/dtoolsStagingTransform";
import { buildDtoolsViewByOption } from "@shared/dtoolsCustomerProposalView";
import { buildInternalSystemSummaryRows, buildV1SystemSummaryTabContent } from "@shared/dtoolsSystemSummary";
import { proposalShowsRetailPriceDisclaimer } from "@shared/proposalRetailPriceDisclaimer";
import type { DtoolsOptionSectionActionsProps } from "@/components/sales/dtools/DtoolsOptionSectionActions";

type Props = {
  systems: DtoolsReviewSystem[];
  footer: DtoolsReviewFooter;
  presentation?: DtoolsProposalPresentation;
  displaySettings?: DtoolsDisplaySettings;
  reviewContext?: DtoolsReviewBuildContext;
  audience?: "customer" | "staff";
  customerPricing?: ProposalCustomerPricingDisplay | null;
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
};

export default function DtoolsV1SystemSummaryTab({
  systems,
  footer,
  presentation,
  displaySettings = {},
  reviewContext,
  customerPricing,
  optionActions,
}: Props) {
  const content = buildV1SystemSummaryTabContent({
    systems,
    footerAdjustments: footer.adjustments,
    displaySettings,
    reviewContext,
    formatMoney: formatDtoolsMoney,
  });

  const effectivePricing =
    customerPricing != null
      ? enrichProposalCustomerPricingWithSnapshot(customerPricing, content.snapshot.proposalTotal)
      : null;

  const optionGroups = buildDtoolsViewByOption(systems, reviewContext, displaySettings);
  const consolidatedSystemRows: ConsolidatedSystemSummaryRow[] = optionGroups
    .filter((group) => !isDtoolsInternalImportSystem(group.system))
    .map((group) => {
      const key = systemContentKey(group.system.systemId, group.system.name) || `option-${group.optionIndex}`;
      const systemView = buildDtoolsReviewSystemView(group.system, reviewContext, group.optionIndex);
      const amount = systemView.overview.systemTotal;
      return {
        key: `amount-${key}`,
        label: group.optionLabel,
        amount,
        customerPricing: effectivePricing
          ? scaleProposalCustomerPricingForSystemTotal(effectivePricing, amount)
          : undefined,
      };
    });
  // Priced lines parked in "Unassigned" are always billable: show them as Services / Other items.
  for (const row of buildInternalSystemSummaryRows(systems, displaySettings)) {
    consolidatedSystemRows.push({
      key: `amount-${row.key}`,
      label: row.label,
      amount: row.amount,
      customerPricing: effectivePricing
        ? scaleProposalCustomerPricingForSystemTotal(effectivePricing, row.amount)
        : undefined,
    });
  }

  const adjustmentsTotal = buildDtoolsReviewProposalTotals({
    systems,
    footerAdjustments: footer.adjustments,
  }).adjustmentsTotal;

  return (
    <ProposalSystemSummaryTab
      content={content}
      presentation={presentation}
      displaySettings={displaySettings}
      consolidatedSystemRows={consolidatedSystemRows}
      proposalPricing={effectivePricing}
      allowIndividualSystemApproval={displaySettings.allowIndividualSystemApproval === true}
      hideCombinedGrandTotal={displaySettings.alternativeOptions === true}
      optionActions={optionActions}
      showRetailPriceDisclaimer={proposalShowsRetailPriceDisclaimer(effectivePricing, adjustmentsTotal)}
      className="dtools-v1-system-summary"
      testId="dtools-v1-system-summary-tab"
    />
  );
}
